// skipcq: JS-W1028
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  comparisonClassificationColor,
  comparisonClassificationLabel,
  comparisonCandidateCurrentName,
} from '../../utils/brewery/beerIngestionComparison';
import ComparisonFieldChangesCell from './ComparisonFieldChanges';
import {
  duplicateApprovedNameCaption,
  isEditableReviewClassification,
  isReviewRowFrozen,
  snapshotBeerForComparisonRow,
} from '../../utils/brewery/beerIngestionReview';
import BeerIngestionReviewRowActions, {
  BeerIngestionReviewStatus,
} from './BeerIngestionReviewRowActions';
import { StyleTypeSelect } from './BeerIngestionStyleTypeStrip';
import {
  SnapshotAbvCell,
  SnapshotDescriptionCell,
  SnapshotIssuesCell,
  SnapshotStatusChip,
  SnapshotStyleCell,
} from './snapshotBeerTableCells';

function ComparisonTableHead({
  reviewMode,
  actionLoading,
  headerChecked,
  headerIndeterminate,
  onToggleSelectAll,
}) {
  return (
    <TableHead>
      <TableRow>
        {reviewMode ? (
          <TableCell padding="checkbox">
            <Checkbox
              size="small"
              disabled={actionLoading}
              checked={Boolean(headerChecked)}
              indeterminate={Boolean(headerIndeterminate)}
              inputProps={{ 'aria-label': 'Select visible selectable rows' }}
              onChange={onToggleSelectAll}
            />
          </TableCell>
        ) : null}
        <TableCell>Classification</TableCell>
        <TableCell>Name (current)</TableCell>
        <TableCell>Field changes</TableCell>
        <TableCell>Style</TableCell>
        <TableCell>ABV</TableCell>
        <TableCell>Description</TableCell>
        <TableCell>Issues</TableCell>
        {reviewMode ? <TableCell>Status</TableCell> : null}
        {reviewMode ? <TableCell>Review</TableCell> : null}
      </TableRow>
    </TableHead>
  );
}

ComparisonTableHead.propTypes = {
  reviewMode: PropTypes.bool,
  actionLoading: PropTypes.bool,
  headerChecked: PropTypes.bool,
  headerIndeterminate: PropTypes.bool,
  onToggleSelectAll: PropTypes.func,
};

function ComparisonClassificationCell({ candidate, snapshotBeer }) {
  const reason = typeof candidate.decisionReason === 'string'
    ? candidate.decisionReason.trim()
    : '';
  const classificationChip = (
    <Chip
      size="small"
      color={comparisonClassificationColor(candidate.classification)}
      label={comparisonClassificationLabel(candidate.classification)}
    />
  );

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {reason ? (
        <Tooltip title={reason} arrow>
          <span>{classificationChip}</span>
        </Tooltip>
      ) : classificationChip}
      <SnapshotStatusChip beer={snapshotBeer} />
    </Box>
  );
}

ComparisonClassificationCell.propTypes = {
  candidate: PropTypes.object.isRequired,
  snapshotBeer: PropTypes.object,
};

function ReviewDraftField({
  candidateKey,
  field,
  label,
  value,
  disabled,
  onChange,
}) {
  return (
    <TextField
      size="small"
      fullWidth
      label={label}
      id={`review-${field}-${candidateKey}`}
      value={value}
      onChange={onChange}
      disabled={disabled}
      inputProps={{
        'data-candidate-key': candidateKey,
        'data-field': field,
      }}
    />
  );
}

ReviewDraftField.propTypes = {
  candidateKey: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

function reviewDisplayName(candidate, draft) {
  const draftName = typeof draft?.name === 'string' ? draft.name.trim() : '';
  if (draftName) {
    return draftName;
  }
  return comparisonCandidateCurrentName(candidate);
}

function ReviewNameCell({
  candidate,
  draft,
  editing,
  disabled,
  onDraftChange,
}) {
  if (!editing) {
    return <TableCell>{reviewDisplayName(candidate, draft)}</TableCell>;
  }
  return (
    <TableCell>
      <ReviewDraftField
        candidateKey={candidate.candidateKey}
        field="name"
        label="Name"
        value={draft?.name ?? ''}
        disabled={disabled}
        onChange={onDraftChange}
      />
    </TableCell>
  );
}

ReviewNameCell.propTypes = {
  candidate: PropTypes.object.isRequired,
  draft: PropTypes.object,
  editing: PropTypes.bool,
  disabled: PropTypes.bool,
  onDraftChange: PropTypes.func,
};

function ReviewStyleCell({
  snapshotBeer,
  styleLabel,
  styleTypeCaption,
  showTypeSelect,
  typeOptions,
  assigningStyleName,
  assigningStyleType,
  assignDisabled,
  onAssignStyleType,
  instanceKey,
  editing,
  draft,
  fieldDisabled,
  onDraftChange,
}) {
  const styleDisplay = styleLabel || snapshotBeer?.normalized?.style || '';
  const saving = assigningStyleName === styleLabel;
  return (
    <TableCell>
      {editing ? (
        <ReviewDraftField
          candidateKey={instanceKey}
          field="style"
          label="Style"
          value={draft?.style ?? ''}
          disabled={fieldDisabled}
          onChange={onDraftChange}
        />
      ) : (
        <Typography variant="body2">{styleDisplay || '—'}</Typography>
      )}
      {styleTypeCaption ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {styleTypeCaption}
        </Typography>
      ) : null}
      {showTypeSelect ? (
        <Box sx={{ mt: 0.5 }}>
          <StyleTypeSelect
            styleName={styleLabel}
            instanceKey={instanceKey}
            label="Style type"
            compact
            value={saving ? (assigningStyleType || '') : ''}
            disabled={assignDisabled}
            saving={saving}
            onChange={onAssignStyleType}
            typeOptions={typeOptions}
          />
        </Box>
      ) : null}
    </TableCell>
  );
}

ReviewStyleCell.propTypes = {
  snapshotBeer: PropTypes.object,
  styleLabel: PropTypes.string,
  styleTypeCaption: PropTypes.string,
  showTypeSelect: PropTypes.bool,
  typeOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
  assigningStyleName: PropTypes.string,
  assigningStyleType: PropTypes.string,
  assignDisabled: PropTypes.bool,
  onAssignStyleType: PropTypes.func,
  instanceKey: PropTypes.string,
  editing: PropTypes.bool,
  draft: PropTypes.object,
  fieldDisabled: PropTypes.bool,
  onDraftChange: PropTypes.func,
};

function ReviewAbvCell({
  snapshotBeer,
  draft,
  editing,
  candidateKey,
  disabled,
  onDraftChange,
}) {
  if (editing) {
    return (
      <TableCell>
        <ReviewDraftField
          candidateKey={candidateKey}
          field="abv"
          label="ABV"
          value={draft?.abv ?? ''}
          disabled={disabled}
          onChange={onDraftChange}
        />
      </TableCell>
    );
  }
  const draftAbv = typeof draft?.abv === 'string' ? draft.abv.trim() : '';
  const snapshotAbv = snapshotBeer?.normalized?.abv;
  const snapshotAbvText = snapshotAbv == null ? '' : String(snapshotAbv);
  if (draftAbv && draftAbv !== snapshotAbvText) {
    return (
      <TableCell>
        <Typography variant="body2">{draftAbv}</Typography>
      </TableCell>
    );
  }
  return <SnapshotAbvCell beer={snapshotBeer} />;
}

ReviewAbvCell.propTypes = {
  snapshotBeer: PropTypes.object,
  draft: PropTypes.object,
  editing: PropTypes.bool,
  candidateKey: PropTypes.string,
  disabled: PropTypes.bool,
  onDraftChange: PropTypes.func,
};

function ComparisonCandidateRow({
  candidate,
  snapshotBeers,
  reviewMode,
  selectable,
  selected,
  decision,
  draft,
  editing,
  reviewLocked,
  actionLoading,
  loadingCandidateKey,
  loadingDecisionAction,
  styleTypeCaption,
  showTypeSelect,
  typeOptions,
  assigningStyleName,
  assigningStyleType,
  onToggleSelected,
  onDecisionClick,
  onDraftChange,
  onToggleEdit,
  onAssignStyleType,
  approveBlockedMessage = '',
}) {
  const snapshotBeer = snapshotBeerForComparisonRow(candidate, snapshotBeers);
  const styleLabel = typeof draft?.style === 'string' ? draft.style.trim() : (
    candidate.currentNormalized?.style || ''
  );
  const frozen = isReviewRowFrozen(decision);
  const canEdit = reviewMode
    && isEditableReviewClassification(candidate.classification)
    && !reviewLocked
    && !frozen;
  const rowEditing = canEdit && Boolean(editing);
  const fieldDisabled = reviewLocked || frozen || actionLoading;

  return (
    <TableRow>
      {reviewMode ? (
        <TableCell padding="checkbox">
          {selectable ? (
            <Checkbox
              size="small"
              checked={Boolean(selected)}
              disabled={reviewLocked || actionLoading}
              inputProps={{
                'aria-label': `Select ${comparisonCandidateCurrentName(candidate)}`,
                'data-candidate-key': candidate.candidateKey,
              }}
              onChange={onToggleSelected}
            />
          ) : null}
        </TableCell>
      ) : null}
      <TableCell>
        <ComparisonClassificationCell candidate={candidate} snapshotBeer={snapshotBeer} />
      </TableCell>
      {reviewMode ? (
        <ReviewNameCell
          candidate={candidate}
          draft={draft}
          editing={rowEditing}
          disabled={fieldDisabled}
          onDraftChange={onDraftChange}
        />
      ) : (
        <TableCell>{comparisonCandidateCurrentName(candidate)}</TableCell>
      )}
      <ComparisonFieldChangesCell candidate={candidate} />
      {reviewMode ? (
        <ReviewStyleCell
          snapshotBeer={snapshotBeer}
          styleLabel={styleLabel}
          styleTypeCaption={styleTypeCaption}
          showTypeSelect={showTypeSelect}
          typeOptions={typeOptions}
          assigningStyleName={assigningStyleName}
          assigningStyleType={assigningStyleType}
          assignDisabled={
            reviewLocked
            || actionLoading
            || Boolean(loadingCandidateKey)
            || Boolean(assigningStyleName)
          }
          onAssignStyleType={onAssignStyleType}
          instanceKey={candidate.candidateKey}
          editing={rowEditing}
          draft={draft}
          fieldDisabled={fieldDisabled}
          onDraftChange={onDraftChange}
        />
      ) : (
        <SnapshotStyleCell beer={snapshotBeer} />
      )}
      {reviewMode ? (
        <ReviewAbvCell
          snapshotBeer={snapshotBeer}
          draft={draft}
          editing={rowEditing}
          candidateKey={candidate.candidateKey}
          disabled={fieldDisabled}
          onDraftChange={onDraftChange}
        />
      ) : (
        <SnapshotAbvCell beer={snapshotBeer} />
      )}
      <SnapshotDescriptionCell description={snapshotBeer?.normalized?.description} />
      <SnapshotIssuesCell beer={snapshotBeer} />
      {reviewMode ? (
        <TableCell>
          <BeerIngestionReviewStatus decision={decision} />
        </TableCell>
      ) : null}
      {reviewMode ? (
        <TableCell>
          <BeerIngestionReviewRowActions
            candidate={candidate}
            decision={decision}
            draft={draft}
            reviewLocked={reviewLocked}
            actionLoading={actionLoading}
            loadingCandidateKey={loadingCandidateKey}
            loadingDecisionAction={loadingDecisionAction}
            editing={rowEditing}
            approveBlockedMessage={approveBlockedMessage}
            onDecisionClick={onDecisionClick}
            onToggleEdit={onToggleEdit}
          />
        </TableCell>
      ) : null}
    </TableRow>
  );
}

ComparisonCandidateRow.propTypes = {
  candidate: PropTypes.object.isRequired,
  snapshotBeers: PropTypes.arrayOf(PropTypes.object),
  reviewMode: PropTypes.bool,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  decision: PropTypes.object,
  draft: PropTypes.object,
  editing: PropTypes.bool,
  reviewLocked: PropTypes.bool,
  actionLoading: PropTypes.bool,
  loadingCandidateKey: PropTypes.string,
  loadingDecisionAction: PropTypes.string,
  styleTypeCaption: PropTypes.string,
  showTypeSelect: PropTypes.bool,
  typeOptions: PropTypes.arrayOf(PropTypes.object),
  assigningStyleName: PropTypes.string,
  assigningStyleType: PropTypes.string,
  onToggleSelected: PropTypes.func,
  onDecisionClick: PropTypes.func,
  onDraftChange: PropTypes.func,
  onToggleEdit: PropTypes.func,
  onAssignStyleType: PropTypes.func,
  approveBlockedMessage: PropTypes.string,
};

export default function BeerIngestionComparisonTable({
  candidates,
  snapshotBeers = [],
  reviewMode = false,
  selectableKeys = [],
  selectedKeys = {},
  decisions = {},
  editingKeys = {},
  reviewLocked = false,
  actionLoading = false,
  loadingCandidateKey = null,
  loadingDecisionAction = '',
  headerChecked = false,
  headerIndeterminate = false,
  assigningStyleName = null,
  assigningStyleType = '',
  styleTypes = [],
  getDraft,
  getStyleTypeCaption,
  isStyleMapped,
  onToggleSelectAll,
  onToggleSelected,
  onDecisionClick,
  onDraftChange,
  onToggleEdit,
  onAssignStyleType,
}) {
  const selectableSet = new Set(selectableKeys);
  const typeOptions = (styleTypes || []).filter((typeRow) => typeRow?.name);
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" stickyHeader aria-label="Snapshot comparison candidates">
        <ComparisonTableHead
          reviewMode={reviewMode}
          actionLoading={actionLoading}
          headerChecked={headerChecked}
          headerIndeterminate={headerIndeterminate}
          onToggleSelectAll={onToggleSelectAll}
        />
        <TableBody>
          {candidates.map((candidate) => {
            const draft = getDraft
              ? getDraft(candidate)
              : { name: '', style: '', abv: '' };
            const styleLabel = typeof draft.style === 'string' ? draft.style.trim() : '';
            const mapped = isStyleMapped ? isStyleMapped(styleLabel) : Boolean(styleLabel);
            const approveBlockedMessage = duplicateApprovedNameCaption(
              candidate.candidateKey,
              draft?.name,
              decisions,
            );
            return (
              <ComparisonCandidateRow
                key={candidate.candidateKey}
                candidate={candidate}
                snapshotBeers={snapshotBeers}
                reviewMode={reviewMode}
                selectable={selectableSet.has(candidate.candidateKey)}
                selected={Boolean(selectedKeys[candidate.candidateKey])}
                decision={decisions[candidate.candidateKey]}
                draft={draft}
                editing={Boolean(editingKeys[candidate.candidateKey])}
                reviewLocked={reviewLocked}
                actionLoading={actionLoading}
                loadingCandidateKey={loadingCandidateKey}
                loadingDecisionAction={loadingDecisionAction}
                styleTypeCaption={getStyleTypeCaption ? getStyleTypeCaption(styleLabel) : null}
                showTypeSelect={reviewMode && Boolean(styleLabel) && !mapped && !reviewLocked}
                typeOptions={typeOptions}
                assigningStyleName={assigningStyleName}
                assigningStyleType={assigningStyleType}
                onToggleSelected={onToggleSelected}
                onDecisionClick={onDecisionClick}
                approveBlockedMessage={approveBlockedMessage || ''}
                onDraftChange={onDraftChange}
                onToggleEdit={onToggleEdit}
                onAssignStyleType={onAssignStyleType}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

BeerIngestionComparisonTable.propTypes = {
  candidates: PropTypes.arrayOf(PropTypes.object).isRequired,
  snapshotBeers: PropTypes.arrayOf(PropTypes.object),
  reviewMode: PropTypes.bool,
  selectableKeys: PropTypes.arrayOf(PropTypes.string),
  selectedKeys: PropTypes.object,
  decisions: PropTypes.object,
  editingKeys: PropTypes.object,
  reviewLocked: PropTypes.bool,
  actionLoading: PropTypes.bool,
  loadingCandidateKey: PropTypes.string,
  loadingDecisionAction: PropTypes.string,
  headerChecked: PropTypes.bool,
  headerIndeterminate: PropTypes.bool,
  assigningStyleName: PropTypes.string,
  assigningStyleType: PropTypes.string,
  styleTypes: PropTypes.arrayOf(PropTypes.object),
  getDraft: PropTypes.func,
  getStyleTypeCaption: PropTypes.func,
  isStyleMapped: PropTypes.func,
  onToggleSelectAll: PropTypes.func,
  onToggleSelected: PropTypes.func,
  onDecisionClick: PropTypes.func,
  onDraftChange: PropTypes.func,
  onToggleEdit: PropTypes.func,
  onAssignStyleType: PropTypes.func,
};
